import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api from '@/utils/api';
import { Zap, Video, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const BG_IMAGE = "https://customer-assets-rejwkqb3.emergentagent.net/job_dragon-sphere-tv/artifacts/4c96144aa38fc356_1793246571889.webp";
const HALLOWEEN_BG = "https://customer-assets-rejwkqb3.emergentagent.net/job_dragon-sphere-tv/artifacts/b747e571a89866f9_ptrpzblzwur31.png";
const WORDMARK_IMAGE = "https://customer-assets-rejwkqb3.emergentagent.net/job_dragon-sphere-tv/artifacts/434e6664f00c6ecf_1793246551377.webp";
const LOGO_IMAGE = "https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/c5tmpjar_Untitled129.png";

export const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [halloween, setHalloween] = useState(false);
  useEffect(() => { api.get('/halloween/season').then((r) => setHalloween(!!r.data.active)).catch(() => {}); }, []);

  const handlePowerUp = (e) => {
    if (user) {
      e.preventDefault();
      toast.success("You're already powered up! Heading to the library...");
      setTimeout(() => navigate('/library'), 1000);
    }
  };

  return (
    <div className="relative isolate min-h-screen" data-testid="landing-page">
      <div
        className={`fixed inset-0 -z-10 bg-cover bg-no-repeat md:bg-center ${halloween ? 'bg-[position:73%_center]' : 'bg-[position:35%_center]'}`}
        style={{ backgroundImage: `url('${halloween ? HALLOWEEN_BG : BG_IMAGE}')` }}
        data-testid="landing-bg"
      />
      <div className="fixed inset-0 -z-10 pointer-events-none bg-gradient-to-b from-[rgba(5,5,16,0.1)] via-[rgba(5,5,16,0.35)] to-[rgba(5,5,16,0.75)]" />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4">
        
        {/* Energy Glow Effects */}
        <div className="absolute top-20 right-10 md:right-20 w-48 h-48 md:w-96 md:h-96 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 md:left-20 w-48 h-48 md:w-96 md:h-96 bg-secondary/20 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto text-center py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="mb-4 md:mb-6" data-testid="hero-title">
              <img
                src={WORDMARK_IMAGE}
                alt="Dragon TV"
                className="mx-auto w-[85vw] max-w-[640px] h-auto object-contain drop-shadow-[0_6px_18px_rgba(0,0,0,0.6)]"
              />
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-100 drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] mb-3 md:mb-4 max-w-2xl mx-auto leading-relaxed px-4">
              Experience the power of Dragon Ball
            </p>
            <p className="text-base sm:text-lg md:text-xl text-gray-200 drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] mb-8 md:mb-12 max-w-3xl mx-auto px-4" data-testid="hero-subtitle">
              The best place to watch Dragon Ball and connect with fellow fans without malicious ads or data miners
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
              <Link
                to="/register"
                onClick={handlePowerUp}
                className="w-full sm:w-auto bg-primary hover:bg-orange-600 text-white font-black uppercase tracking-widest px-8 md:px-10 py-4 md:py-5 clip-path-slant hover:scale-105 transition-transform duration-200 shadow-[0_0_30px_rgba(248,91,26,0.6)] text-base md:text-lg text-center flex items-center justify-center"
                data-testid="hero-register-btn"
              >
                <img src={LOGO_IMAGE} alt="" className="w-6 h-6 md:w-7 md:h-7 mr-2 object-contain" />
                Power Up
              </Link>
              <Link
                to="/library"
                className="w-full sm:w-auto bg-transparent border-2 border-secondary text-secondary hover:bg-secondary/10 font-bold uppercase tracking-widest px-8 md:px-10 py-4 md:py-5 rounded-none hover:shadow-[0_0_20px_rgba(0,71,171,0.5)] transition-all text-base md:text-lg text-center"
                data-testid="hero-library-btn"
              >
                <Video className="inline w-5 h-5 md:w-6 md:h-6 mr-2" />
                Watch Now
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-32 relative px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-heading uppercase text-white mb-4 tracking-tighter" data-testid="features-title">
              Unleash The <span className="text-accent">Power</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-400 px-4">Everything you need for the ultimate Dragon Ball experience</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: Video,
                title: 'Epic Episodes',
                description: 'Watch the entirety of Dragon Ball start to finish, including variations like the fan-loved Faulconer soundtrack',
                color: 'primary',
              },
              {
                icon: MessageCircle,
                title: 'Live Chat',
                description: 'Connect with fellow fans in real-time. Chat globally or discuss specific episodes with the community',
                color: 'secondary',
              },
              {
                icon: Zap,
                title: 'Customizable Experience',
                description: 'Customize your profile and site themes through the shop system for a truly unique experience',
                color: 'accent',
              },
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden bg-card/70 backdrop-blur-md border border-white/10 hover:border-primary/50 transition-all duration-300 hover:-translate-y-2 rounded-bl-2xl rounded-tr-2xl p-8"
                  data-testid={`feature-card-${index}`}
                >
                  <div className={`inline-flex p-4 rounded-full mb-6 bg-${feature.color}/10`}>
                    <Icon className={`w-8 h-8 text-${feature.color}`} />
                  </div>
                  <h3 className="text-2xl font-heading uppercase mb-4 text-white">{feature.title}</h3>
                  <p className="text-gray-200 leading-relaxed">{feature.description}</p>
                  
                  {/* Hover glow effect */}
                  <div className={`absolute inset-0 bg-${feature.color}/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-5xl md:text-7xl font-heading uppercase italic text-white mb-8 tracking-tight" data-testid="cta-title">
            Ready to Power Up?
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            Join Dragon TV today and become part of the ultimate Dragon Ball streaming community
          </p>
          <Link
            to="/register"
            onClick={handlePowerUp}
            className="inline-block bg-accent hover:bg-yellow-500 text-black font-black uppercase tracking-widest px-12 py-6 rounded-full hover:scale-105 transition-transform duration-200 shadow-[0_0_40px_rgba(255,215,0,0.4)] text-xl"
            data-testid="cta-register-btn"
          >
            <Zap className="inline w-7 h-7 mr-2" />
            Start Watching
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
